import { Box, Stack, Tab, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import CustomTable from "../../common/tables/Table";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";

import FilterLinkAction from "../../common/filter-action/FilterLinkAction";
import { routerPaths } from "../../../constants/Paths";
import { dateFormatter } from "../../../utils/date-formatter";
import { CallOutgoing, DocumentText, EmptyWallet, Microphone, People } from "iconsax-react";
import { useUrl } from "../../../hooks/urlEncoding";
import { WalletListColumnFilterable } from "../../../services/Endpoints/wallet/WalletListEndpoint";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";
import {
  BroadcastingListColumnFilterable,
  BroadcastingListColumnSortable,
} from "../../../services/Endpoints/broadcast";
import LinkWithPopover from "../common/LinkWithPopover";
import {
  CallDetailListColumnFilterable,
  CallDetailListColumnSortable,
  CallDetailListRequest,
  PbxCallDetailStatusEnum,
} from "../../../services/Endpoints/broadcast/PbxCallDetails";
import InfoBadge, { OriginEnum } from "../../common/info-badge/InfoBadge";
import { TabContext, TabList } from "@mui/lab";
import MuiTabPanel from "../../common/tab-panel/TabPanel";
import ViewAction from "../../common/view-action/ViewAction";
import MuiBaseModal from "../../common/modal/MuiModal";
import { PbxCallDetailRecord } from "../../../services/Schemas/PBXCallDetail";
import { PartnerSchema } from "../../../services/Schemas/Partner";
import {
  PartnerListColumnFilterable,
  PartnerListColumnSortable,
} from "../../../services/Endpoints/partners/PartnerEndpoint";
import { LogListColumnFilterable, LogListColumnSortable } from "../../../services/Endpoints/logs/LogListEndpoints";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import useTableSchema from "../../../hooks/useTableSchema";
import { ITableSchema } from "../../../utils/types";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import useUrlSort from "../../../hooks/useUrlSort";

const statusOptions = {
  [PbxCallDetailStatusEnum.CREATED]: "ایجاد شده",
  [PbxCallDetailStatusEnum.WAITING_FOR_SEND]: "در انتظار ارسال",
  [PbxCallDetailStatusEnum.SENDING]: "در حال ارسال",
  [PbxCallDetailStatusEnum.FAILED]: "ناموفق",
  [PbxCallDetailStatusEnum.ANSWERED]: "پاسخ داده شده",
  [PbxCallDetailStatusEnum.BLOCKED]: "بلاک شده",
  [PbxCallDetailStatusEnum.NO_ANSWER]: "بی پاسخ",
  [PbxCallDetailStatusEnum.BUSY]: "مشغول",
  [PbxCallDetailStatusEnum.REJECTED]: "رد شده",
  [PbxCallDetailStatusEnum.UNREACHABLE]: "خارج از دسترس",
  [PbxCallDetailStatusEnum.DEVICE_OFF]: "خاموش",
  [PbxCallDetailStatusEnum.INVALID_NUMBER]: "شماره نامعتبر",
  [PbxCallDetailStatusEnum.CANCELED]: "کنسل شده",
};

const pbxCallDetailsColumns: ITableSchema[] = [
  {
    key: "id",
    label: "شناسه",
    minWidth: 100,
    align: "center",
    FilterComponent: NumberFilter,
    sortable: true,
  },
  {
    key: "number",
    label: "شماره",
    minWidth: 100,
    align: "center",
    FilterComponent: TextFilter,
  },
  {
    key: "start_time",
    label: "ارسال تماس",
    minWidth: 100,
    align: "center",
    FilterComponent: TextFilter,
  },

  {
    key: "updated_at",
    label: " ویرایش",
    minWidth: 100,
    align: "center",
    FilterComponent: DateTimeFilter,
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },

  {
    key: "scheduled_time",
    label: " زمان برنامه ریزی شده",
    minWidth: 100,
    align: "center",
    FilterComponent: DateTimeFilter,
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "pickup_time",
    label: " برداشت",
    minWidth: 100,
    align: "center",
    FilterComponent: TextFilter,
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "sent_time",
    label: " ارسال تماس",
    minWidth: 100,
    align: "center",
    FilterComponent: DateTimeFilter,
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "end_time",
    label: " پایان تماس",
    minWidth: 100,
    align: "center",
    FilterComponent: DateTimeFilter,
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "waiting_seconds",
    label: "مدت انتظار",
    minWidth: 100,
    align: "center",
    FilterComponent: DateTimeFilter,
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "billable_seconds",
    label: "مدت مکالمه",
    minWidth: 100,
    align: "center",
    FilterComponent: DateTimeFilter,
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "caller_id",
    label: "شماره مبدا",
    minWidth: 100,
    align: "center",
    FilterComponent: TextFilter,
  },
  {
    key: "status",
    label: "وضعیت",
    minWidth: 100,
    align: "center",
    FilterComponent: SelectionFilter,
    options: statusOptions,
  },

  {
    key: "operations",
    label: "عملیات",
    minWidth: 100,
    align: "center",
  },
];

interface IPayment {
  id: number;
  number: string;
  duration_seconds: number;
  partner: null | PartnerSchema | ReactNode;
  answer_time: string | null;
  pbx_server_id: number | null;
  pbx_trunk_id: number | null;
  extension: string | null;
  voice_broadcast_id: number;
  created_at: string;
  status: ReactNode;
  operations: string | ReactNode;
  updated_at: string | null;
  pickup_time: string | null;
  sent_time: string | null;
  end_time: string | null;
  caller_id: string;
}
interface IPaymentSchema extends IPayment {
  partner: null | PartnerSchema;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
}

const displayedData = {
  id: "آیدی",
  voice_broadcast_id: "آیدی کمپین",
  voice_broadcast_number_id: "آیدی شماره کمپین",
  partner_id: "آیدی شریک کاری",
  pbx_server_id: "آیدی سرور تماس",
  pbx_trunk_id: "آیدی ترانک",
  pbx_channel_reference_code: "کد مرجع کانال",
  number: "شماره",
  extension: "داخلی",
  pickup_time: "زمان برداشت سرور",
  scheduled_time: "زمان برنامه ریزی شده",
  sent_time: "زمان ارسال",
  start_time: "زمان شروع",
  answer_time: "زمان پاسخ",
  end_time: "زمان اتمام",
  unique_id: "شناسه خاص",
  user_field: "عدد ورودی کاربر",
  caller_id: "آیدی تماس گیرنده",
  waiting_seconds: "طول انتظار",
  billable_seconds: "زمان قابل پرداخت",
  duration_seconds: "طول تماس",
  status: "وضعیت",
  created_at: "تاریخ ایجاد",
  updated_at: "تاریخ ویرایش",
  partner: "شریک کاری",
};

export default function PbxCallDetailTableComponent({
  walletReload,
  setWalletReload,
}: Readonly<ISupportTabsComponent>) {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value, setValue] = useState("1");
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [callDetail, setCallDetails] = useState<
    Omit<IPaymentSchema, "operations"> | undefined
  >(undefined);
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const [dataPayment, setDataPayment] = useState<IPayment[] | []>([]);
  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();
  const { filtersSchema, columns } = useTableSchema(pbxCallDetailsColumns);
  const location = useLocation();

  const getDataPayment = async () => {
    try {
      setWaiting(true);
      const paymentListRequest = new CallDetailListRequest(protectedInstance);
      const resp = await paymentListRequest.list({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.pbx_call_detail_records.map((data) => ({
        id: data.id,
        number: data.number,
        updated_at: data.updated_at,
        pickup_time: data.pickup_time,
        sent_time: data.sent_time,
        scheduled_time: data.scheduled_time,
        end_time: data.end_time,
        duration_seconds: data.duration_seconds,
        start_time:data.start_time,
        waiting_seconds:data.waiting_seconds,
        billable_seconds:data.billable_seconds,
        caller_id: data.caller_id,
        partner: data.partner,
        answer_time: data.answer_time,
        pbx_server_id: data.pbx_server_id,
        pbx_trunk_id: data.pbx_trunk_id,
        extension: data.extension,
        voice_broadcast_id: data.voice_broadcast_id,
        created_at: data.created_at,
        status: (
          <InfoBadge
            origin={OriginEnum.BROADCASTING_NUMBER}
            status={data.status as "pending"}
          />
        ),
        operations: (
          <Stack
            direction="row"
            gap="1px"
            alignItems="center"
            justifyContent="center"
          >
            <ViewAction handleView={() => handleView(data)} />
            <LinkWithPopover
              disabled={!can(AccessEnums.AdminVoiceBroadcastsList)}
              title="پیام صوتی"
            >
              <Stack
                sx={{
                  padding: "4px",
                  borderRadius: "50%",
                  width: "fit-content",
                  cursor: "pointer",
                  textDecoration: "none",
                  color: (theme) => theme.palette.complement[400],
                  "&:hover": {
                    color: (theme) => theme.palette.others.link,
                    // backgroundColor: (theme) => theme.palette.others.doneBg,
                  },
                }}
                component={Link}
                to={queryStringUrl(routerPaths.VOICE_BROADCASTS, {
                  filters: {
                    [BroadcastingListColumnFilterable.ID]: {
                      column: BroadcastingListColumnFilterable.ID,
                      operator: FilterOperatorEnum.EQUAL,
                      value: data.voice_broadcast_id,
                    },
                  },
                  sorts: {
                    [BroadcastingListColumnSortable.ID]: {
                      column: BroadcastingListColumnSortable.ID,
                      direction: SortDirectionEnum.DESC,
                    },
                  },
                })}
              >
                <Microphone size="20" />
              </Stack>
            </LinkWithPopover>

            <LinkWithPopover
              disabled={!can(AccessEnums.AdminPartnerList)}
              title="شریکها"
            >
              <Stack
                sx={{
                  padding: "4px",
                  borderRadius: "50%",
                  width: "fit-content",
                  cursor: "pointer",
                  textDecoration: "none",
                  color: (theme) => theme.palette.complement[400],
                  "&:hover": {
                    color: (theme) => theme.palette.others.link,
                    // backgroundColor: (theme) => theme.palette.others.doneBg,
                  },
                }}
                component={Link}
                to={queryStringUrl(routerPaths.PARTNERS, {
                  filters: {
                    [PartnerListColumnFilterable.ID]: {
                      column: PartnerListColumnFilterable.ID,
                      operator: FilterOperatorEnum.EQUAL,
                      value: data?.partner?.id,
                    },
                  },
                  sorts: {
                    [PartnerListColumnSortable.ID]: {
                      column: PartnerListColumnSortable.ID,
                      direction: SortDirectionEnum.DESC,
                    },
                  },
                })}
              >
                <People size="20" />
              </Stack>
            </LinkWithPopover>

            <LinkWithPopover
              disabled={!can(AccessEnums.AdminVoiceBroadcastNumbersList)}
              title="شماره ها"
            >
              <Stack
                sx={{
                  padding: "4px",
                  borderRadius: "50%",
                  width: "fit-content",
                  cursor: "pointer",
                  textDecoration: "none",
                  color: (theme) => theme.palette.complement[400],
                  "&:hover": {
                    color: (theme) => theme.palette.others.link,
                    // backgroundColor: (theme) => theme.palette.others.doneBg,
                  },
                }}
                component={Link}
                to={queryStringUrl(routerPaths.BROAD_NUMBER, {
                  filters: {
                    [PartnerListColumnFilterable.ID]: {
                      column: PartnerListColumnFilterable.ID,
                      operator: FilterOperatorEnum.EQUAL,
                      value: data.voice_broadcast_number_id,
                    },
                  },
                  sorts: {
                    [PartnerListColumnSortable.ID]: {
                      column: PartnerListColumnSortable.ID,
                      direction: SortDirectionEnum.DESC,
                    },
                  },
                })}
              >
                <CallOutgoing size="20" />
              </Stack>
            </LinkWithPopover>
            <LinkWithPopover
              title="لاگ ها"
              disabled={!can(AccessEnums.AdminLogsList)}
            >
              <Stack
                sx={{
                  padding: "4px",
                  borderRadius: "50%",
                  width: "fit-content",
                  cursor: "pointer",
                  color: (theme) => theme.palette.complement[400],
                  "&:hover": {
                    color: (theme) => theme.palette.others.link,
                    // backgroundColor: (theme) => theme.palette.others.doneBg,
                  },
                }}
                component={Link}
                to={queryStringUrl(routerPaths.LOGS, {
                  filters: {
                    [LogListColumnFilterable.REFERENCE_MODEL]: {
                      column: LogListColumnFilterable.REFERENCE_MODEL,
                      operator: FilterOperatorEnum.EQUAL,
                      value: data.id,
                    },
                    [LogListColumnFilterable.MODEL]: {
                      column: LogListColumnFilterable.MODEL,
                      operator: FilterOperatorEnum.EQUAL,
                      value: "pbx_call_detail_records",
                    },
                  },
                  sorts: {
                    [LogListColumnSortable.ID]: {
                      column: LogListColumnSortable.ID,
                      direction: SortDirectionEnum.DESC,
                    },
                  },
                })}
              >
                <DocumentText size="20" />
              </Stack>
            </LinkWithPopover>
          </Stack>
        ),
      }));
      setDataPayment(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataPayment();
  }, [walletReload, page, rowsPerPage, value, location.search]);

  const handleView = (call: PbxCallDetailRecord) => {
    setCallDetails(call);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataPayment}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
      <MuiBaseModal
        onClose={() => setCallDetails(undefined)}
        open={!!callDetail}
        title="جزئیات تماس"
      >
        <Stack>
          <Stack
            gap="24px"
            direction="column"
            sx={{
              bgcolor: (theme) => theme.palette.gray[100],
              borderWidth: "1px",
              borderStyle: "solid",
              py: "18px",
              px: "16px",
              borderRadius: "8px",
            }}
          >
            {Object.keys(displayedData).map((itm, i) => (
              <Stack
                key={i}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap="16px"
                flexWrap="wrap"
              >
                <Typography
                  variant="h6MediumBold"
                  color={(theme) => theme.palette.dark[40]}
                >
                  {displayedData[itm as "created_at"]}
                </Typography>
                <Typography
                  variant="h6MediumBold"
                  color={(theme) => theme.palette.dominant.main}
                >
                  {callDetail && (
                    <>
                      {itm == "partner" ? (
                        <>
                          {callDetail.partner && (
                            <LinkWithPopover
                              title={callDetail.partner?.caption ?? ""}
                            >
                              <Stack
                                sx={{
                                  padding: "4px",
                                  borderRadius: "50%",
                                  width: "fit-content",
                                  cursor: "pointer",
                                  justifyContent: "center",
                                  textDecoration: "none",
                                  color: (theme) =>
                                    theme.palette.complement[400],
                                  "&:hover": {
                                    color: (theme) => theme.palette.others.link,
                                    // backgroundColor: (theme) => theme.palette.others.doneBg,
                                  },
                                }}
                                component={Link}
                                to={queryStringUrl(routerPaths.PARTNERS, {
                                  filters: [
                                    {
                                      column: PartnerListColumnFilterable.ID,
                                      operator: FilterOperatorEnum.EQUAL,
                                      value: callDetail?.partner?.id ?? "1",
                                    },
                                  ],
                                })}
                              >
                                {callDetail?.partner?.caption}
                              </Stack>
                            </LinkWithPopover>
                          )}
                        </>
                      ) : itm == "status" ? (
                        <InfoBadge
                          origin={OriginEnum.BROADCASTING_NUMBER}
                          status={
                            (callDetail?.status ?? "pending") as "pending"
                          }
                        />
                      ) : (
                        <>
                          {itm.includes("at") || itm.includes("time")
                            ? callDetail[itm as "created_at"] &&
                              dateFormatter({
                                lang: "fa",
                                date: callDetail[itm as "created_at"],
                              })
                            : callDetail[itm as "created_at"]}
                        </>
                      )}
                    </>
                  )}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Stack
            width="100%"
            direction="row"
            justifyContent={{ xs: "center", sm: "end" }}
            mb="16px"
          ></Stack>
        </Stack>
      </MuiBaseModal>
    </Box>
  );
}
